<template>

  <div>

    <div class="iframe-container" :style="{height: iframe_map_height, width: '100%'}">

      <!-- This first iframe is purely for maintaining an active session with AKLC system -->
      <iframe v-show="false" v-if="iframe_login_url" :src="iframe_login_url" frameborder="0" @load="onLoginLoaded" :key="iframe_login_unique_id"></iframe>

      <!-- This is the actual map iframe -->
      <iframe v-if="iframe_map_url" :src="iframe_map_url" frameborder="0" :style="{height: iframe_map_height, width: '100%'}"></iframe>

    </div>

  </div>

</template>

<style scoped>

  .iframe-container {
    background: url('https://cdnjs.cloudflare.com/ajax/libs/file-uploader/3.7.0/processing.gif') center center no-repeat;
  }

</style>

<script>

import _ from 'lodash';
import Vue from 'vue'
import VueResource from 'vue-resource'
Vue.use(VueResource);

export default {

  props: {
    config: {type: Object, required: true},
  },

  data() {

    return {

      // We need access to legacy system config values
      config_legacy: {'aklc_gis': {}},

      // The URLs for the iframes (login and map)
      iframe_login_url: null,
      iframe_login_unique_id: this.getUniqueId('iframe_login'),
      iframe_login_interval: null, // For window.clearInterval()
      iframe_map_url: null,
      iframe_map_height: '500px',
      iframe_map_loaded: false,  // So we only load the map iframe once per mount (login iframe may reload)
    };
  },

  mounted() {

    const vm = this;

    this.getLegacyConfig()
      .then(config => {

        vm.$data.config_legacy = config;

        this.onConfigLoaded();
      })
      .catch(err => {

        vm.$buefy.toast.open({
            duration: 3000,
            message: `Failed to fetch map configuration values.`,
            type: 'is-danger',
          });
      });
  },

  beforeDestroy() {

    // ----------------------------------------------------
    // START Clean up event listeners and intervals etc.
    // ----------------------------------------------------

    if (this.$data.iframe_login_interval) window.clearInterval(this.$data.iframe_login_interval);

    window.removeEventListener('message', this.onPostMessage, false);

    // ----------------------------------------------------
    // END Clean up event listeners and intervals etc.
    // ----------------------------------------------------
  },

  methods: {

    getLegacyConfig() {

      let queryParams = {'development': process.env.ENVIRONMENT == 'local' ? 1 : 0};

      return Vue.http.get(process.env.LEGACY_CONFIG_URL, { params: queryParams, credentials: true })
        .then(response => {

          const conf = response.body;

          return Promise.resolve(conf);
        })
        .catch(err => {

          return Promise.reject(err);
        });
    },

    getUniqueId(prefix) {

      prefix = _.isString(prefix) ? prefix : '';

      return _.uniqueId(prefix);
    },

    paramifyBasic(obj) {

      let str_parts = [];

      _.forEach(obj, (v, k) => {

        str_parts.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
      });

      return str_parts.join('&');
    },

    onConfigLoaded() {

      const vm = this;
      const aklc_gis = _.get(this.$data, 'config_legacy.aklc_gis', {});

      // Start the remote login process by opening an iframe with correct app credentials
      // (map iframe can then be activated on successful load event from login iframe)
      const url_base = _.get(aklc_gis, 'webappUrl');
      const url_login = _.get(aklc_gis, 'webapp_login_url');
      const appIdentity = _.get(aklc_gis, 'appIdentity', '');
      const clientid = _.get(aklc_gis, 'clientid', '');
      const params_login = {appIdentity, clientid};

      // Set the map iframe height to avoid page jumps
      this.$data.iframe_map_height = _.get(this.config, 'map_height', '500px');

      this.$data.iframe_login_url = url_login + '?' + this.paramifyBasic(params_login);

      // Refresh the login session every 30 minutes since it expires every 60 minutes
      // (a simple trick is to update the login iframe's :key to force a re-render)
      this.$data.iframe_login_interval = window.setInterval(function() {

        if (vm.$data.iframe_login_url) {

          vm.$data.iframe_login_unique_id = vm.getUniqueId('iframe_login');
        }

      }, 1800000);

      // Look out for a postMessage from the map iframe
      window.addEventListener('message', this.onPostMessage, false);
    },

    onLoginLoaded() {

      const vm = this;
      const aklc_gis = _.get(this.$data, 'config_legacy.aklc_gis', {});

      // The login iframe may reload to maintain session but we don't want this to trigger a map reload
      if (vm.$data.iframe_map_loaded) return;

      // @todo - remove fallback values once done developing...
      const url_base = _.get(aklc_gis, 'webappUrl');
      const url_map = _.get(aklc_gis, 'webapp_map_url');
      const webmapid = _.get(aklc_gis, 'webmapid');
      const layer_base_url = _.get(aklc_gis, 'layerBaseUrl');
      const layer_number_map = _.get(aklc_gis, 'layer_number_map');
      const wkid = 2193;  // Esri WKID (Projection)
      const asset_type = String(_.get(this.config, 'asset_type') || '').toUpperCase();
      const asset_id = _.get(this.config, 'asset_id');
      const latitude = _.get(this.config, 'latitude', 5919861.289282726);
      const longitude = _.get(this.config, 'longitude', 1756939.3113660014);

      let params_map = {
        'webmapid': webmapid,
        'zoom': 11,
        'mode': 'selectequipment'
      }

      // If we know the asset type and ID in advance we can attempt to highlight it
      // (otherwise we may be able to provide co-ordinates to center the map on)
      if (asset_type && asset_id) {

        const layer_number = _.get(layer_number_map, asset_type);
        const layer_url = (layer_number && layer_base_url) ? `${layer_base_url}/${layer_number}` : false;

        if (asset_id && layer_url) {

          params_map['highlight'] = 'true';
          params_map['layer'] = layer_url;
          params_map['assetid'] = _.trim(asset_id).replace(/^0+/, '');  // remove leading zeroes
        }
      }
      else if (latitude && longitude) {

        // The "center"  parameter is a CSV
        params_map['center'] = [longitude, latitude, wkid].join(',');
      }

      vm.$data.iframe_map_url = url_map + '?' + this.paramifyBasic(params_map);
      vm.$data.iframe_map_loaded = true;
    },

    onPostMessage(e) {

      const aklc_gis = _.get(this.$data, 'config_legacy.aklc_gis', {});
      const post_message_origin = _.get(aklc_gis, 'postMessageOrigin');

      let data = {};

      // Only trust expected domain names for this post message.
      if (e.origin.toLowerCase() !== post_message_origin) return;

      try {

        const payload = JSON.parse(e.data);
        const action = payload.action;
        const mode = payload.action;
        const attributes = _.get(payload, 'message.attributes', {});

        if (mode == 'selectequipment' && action == 'selectequipment' && attributes) {

          data['SW_SAP_ID'] = null;
          data['SW_DESCRIPTION'] = null;
          data['geometry_x'] = _.get(payload, 'message.geometry.x', null);
          data['geometry_y'] = _.get(payload, 'message.geometry.y', null);
          data['asset_class'] = null;

          // Now we need to loop through attributes to find certain attributes which have dynamic names.
          // Sample attribute name: SAPGEO.MASTER.{dynamic}.SW_SAP_ID
          const needles = ['SW_SAP_ID', 'SW_DESCRIPTION'];

          _.forEach(attributes, (value, name) => {

            _.forEach(needles, (needle, k) => {

              if (name.substring(name.length - needle.length).toLowerCase() == needle.toLowerCase()) {

                data[needle] = value;
              }
            });
          });

          // We unfortunately need to reverse engineer the selected asset's type from its description text.
          const locationIds = _.get(this.$data, 'config_legacy.aklc_gis.enum.locationId', {});

          if (data['SW_DESCRIPTION'] && locationIds) {

            let asset_class = _.findKey(locationIds, (description) => {

              return description.toLowerCase() == data['SW_DESCRIPTION'].toLowerCase();
            });

            // We only want the "PIPE" part of something like "SW-S-PIPE"
            asset_class = asset_class ? asset_class.split('-') : [];

            asset_class = asset_class[2] || false;

            data['asset_class'] = asset_class;
          }
        }
      }
      catch (err) {

        // Not sure about this one yet...
      }

      if (data) this.$emit('postmessage', data);
    }
  }
};

</script>


// WEBPACK FOOTER //
// src/components/AklcAssetMap.vue